/**
 * @fileoverview Tipos e interfaces para el módulo API Munzen
 * @author Omar Sánchez Martinez
 * @since 15/10/2025
 */

/**
 * Respuesta de error estructurada del API
 */
export interface ApiErrorResponse {
  /** Indica si la operación fue exitosa (siempre false en errores) */
  success: boolean;
  /** Indica si el error debe mostrarse al cliente */
  showErrorToClient: boolean;
  /** Mensaje de error descriptivo o errores de validación por campo */
  dataError: string | Record<string, string[]>;
  /** UUID para tracking del error en logs del servidor */
  errorTrackingUUID: string | null;
  /** Indica si el mensaje de error debe persistir en pantalla */
  persistentErrorMessage: boolean;
  /** Título opcional del error */
  title: string | null;
}

/**
 * Respuesta del endpoint de refresh token
 */
export interface RefreshTokenResponse {
  /** Nuevo token de acceso */
  token: string;
  /** Tiempo de expiración en segundos */
  expires_in: number;
}

/**
 * Configuración de la instancia de axios
 */
export interface ApiConfig {
  /** URL base del API */
  baseURL: string;
  /** Timeout en milisegundos */
  timeout: number;
  /** Headers por defecto */
  headers: {
    'Content-Type': string;
  };
}

/**
 * Clase de error personalizada para errores del API
 * Extiende Error nativo para mantener stack trace
 */
export class ApiError extends Error {
  /** Indica si la operación fue exitosa (siempre false) */
  public readonly success: boolean;
  /** Indica si el error debe mostrarse al cliente */
  public readonly showErrorToClient: boolean;
  /** Mensaje de error del API o errores de validación por campo */
  public readonly dataError: string | Record<string, string[]>;
  /** UUID para tracking del error */
  public readonly errorTrackingUUID: string | null;
  /** Indica si el mensaje debe persistir */
  public readonly persistentErrorMessage: boolean;
  /** Título opcional del error */
  public readonly title: string | null;
  /** Código de estado HTTP */
  public readonly status: number;

  /**
   * Constructor de ApiError
   * @param errorResponse - Respuesta de error del API
   * @param status - Código de estado HTTP
   */
  constructor(errorResponse: ApiErrorResponse, status: number) {
    // Para el mensaje de Error, usar string o serializar el objeto
    const errorMessage =
      typeof errorResponse.dataError === 'string'
        ? errorResponse.dataError
        : JSON.stringify(errorResponse.dataError);

    super(errorMessage);
    this.name = 'ApiError';
    this.success = errorResponse.success;
    this.showErrorToClient = errorResponse.showErrorToClient;
    this.dataError = errorResponse.dataError;
    this.errorTrackingUUID = errorResponse.errorTrackingUUID;
    this.persistentErrorMessage = errorResponse.persistentErrorMessage;
    this.title = errorResponse.title;
    this.status = status;

    // Mantener el prototype chain correcto para instanceof
    Object.setPrototypeOf(this, ApiError.prototype);
  }

  /**
   * Serializa el error a JSON
   * @returns Objeto JSON con las propiedades del error
   */
  toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      message: this.message,
      success: this.success,
      showErrorToClient: this.showErrorToClient,
      dataError: this.dataError,
      errorTrackingUUID: this.errorTrackingUUID,
      persistentErrorMessage: this.persistentErrorMessage,
      title: this.title,
      status: this.status,
    };
  }
}

/**
 * Type guard para verificar si un error es una instancia de ApiError
 * @param error - Error a verificar
 * @returns true si el error es ApiError, false en caso contrario
 *
 * @example
 * try {
 *   await apiMunzen.get('/endpoint');
 * } catch (error) {
 *   if (isApiError(error)) {
 *     console.log(error.dataError);
 *     console.log(error.status);
 *   }
 * }
 */
export const isApiError = (error: unknown): error is ApiError => {
  return error instanceof ApiError;
};
